import { useCallback } from 'react';

import {
  createQuestionGroup,
  deleteQuestionGroup,
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  updateQuestionGroup,
} from '@api/instant_survey';
import { toast } from '@utilities';

import { DEFAULT_QUESTION_GROUP_NAME } from './constants';
import { createQuestionKey, isQuestionGroupNameTaken } from './helpers';
import { firstGroupSubtreeQuestionIndex } from '../components/Questionnaire/utilities';

/**
 * pruneInvalidLogic
 * Logic can only depend on questions that come BEFORE it. Moving a question can break that — most
 * commonly by moving a referenced question into the very question group that references it, or
 * below the question that references it. This removes any logic row left pointing forward.
 * @param {Object} [options.questionGroups] question groups keyed by id, used to locate a group by
 *   its subtree — a parent group may hold nothing but child groups
 * @param {Record<string, import('@api/instant_survey/types').QuestionLogic>} options.questionLogic
 * @param {import('@api/instant_survey/types').Question[]} options.questions
 * @returns {{ changed: boolean, questionLogic: Object }}
 */
export const pruneInvalidLogic = ({
  questionGroups = {},
  questionLogic = {},
  questions = [],
} = {}) => {
  const indexById = new Map(questions.map((question, index) => [String(question.id), index]));

  const pruneRows = (rows, startIndex) =>
    rows.filter(({ question }) => {
      if (!question?.id) return true;
      const sourceIndex = indexById.get(String(question.id));
      return sourceIndex === undefined || sourceIndex < startIndex;
    });

  const pruneLogic = (logic, startIndex) => {
    let changed = false;
    const next = { ...logic };

    QUESTION_GROUP_LOGIC_BRANCHES.forEach((branch) => {
      const branchRows = logic[branch]?.logic;
      if (!Array.isArray(branchRows)) return;
      const filtered = pruneRows(branchRows, startIndex);
      if (filtered.length === branchRows.length) return;
      changed = true;
      next[branch] = { ...logic[branch], logic: filtered };
    });

    if (logic.option) {
      const nextOption = {};
      let optionChanged = false;
      Object.entries(logic.option).forEach(([answerId, optionLogic]) => {
        const optionRows = optionLogic?.logic;
        if (!Array.isArray(optionRows)) {
          nextOption[answerId] = optionLogic;
          return;
        }
        const filtered = pruneRows(optionRows, startIndex);
        if (filtered.length === optionRows.length) {
          nextOption[answerId] = optionLogic;
          return;
        }
        optionChanged = true;
        // Drop the answer entry entirely once it has no rows left.
        if (filtered.length > 0) {
          nextOption[answerId] = { ...optionLogic, logic: filtered };
        }
      });
      if (optionChanged) {
        changed = true;
        if (Object.keys(nextOption).length > 0) {
          next.option = nextOption;
        } else {
          delete next.option;
        }
      }
    }

    return { changed, logic: changed ? next : logic };
  };

  let changed = false;
  const nextQuestionLogic = {};

  Object.entries(questionLogic).forEach(([key, logic]) => {
    if (!logic) {
      nextQuestionLogic[key] = logic;
      return;
    }

    if (key === QUESTION_GROUP_LOGIC) {
      const nextGroupLogic = {};

      Object.entries(logic).forEach(([groupId, groupLogic]) => {
        // Group logic is stored one level deeper, keyed by group id. Everything it references must
        // come before the group's first question, so a question moved INTO the group is now
        // invalid. The group starts where its subtree starts: a parent group may hold nothing but
        // child groups and so have no direct members of its own.
        const startIndex = firstGroupSubtreeQuestionIndex({
          groupId,
          questionGroups,
          questions,
        });
        if (startIndex === -1 || !groupLogic) {
          nextGroupLogic[groupId] = groupLogic;
          return;
        }

        const result = pruneLogic(groupLogic, startIndex);
        if (!result.changed) {
          nextGroupLogic[groupId] = groupLogic;
          return;
        }

        changed = true;
        // A group whose every branch lost all its rows loses its entry entirely.
        if (
          QUESTION_GROUP_LOGIC_BRANCHES.some((branch) => result.logic[branch]?.logic?.length > 0)
        ) {
          nextGroupLogic[groupId] = result.logic;
        }
      });

      // Omitting the key when empty drops QUESTION_GROUP_LOGIC from the result.
      if (Object.keys(nextGroupLogic).length > 0) {
        nextQuestionLogic[key] = nextGroupLogic;
      }
      return;
    }

    // A question's own logic — everything it references must come before the question itself.
    const startIndex = indexById.get(String(key));
    if (startIndex === undefined) {
      nextQuestionLogic[key] = logic;
      return;
    }

    const result = pruneLogic(logic, startIndex);
    if (result.changed) changed = true;
    nextQuestionLogic[key] = result.logic;
  });

  if (!changed) return { changed: false, questionLogic };
  return { changed: true, questionLogic: nextQuestionLogic };
};

/**
 * Shows a toast letting users know question logic was pruned after a question group change (moving a
 * question into a group or nesting a group). Mirrors displayQuestionLogicUpdatedToast.
 * @returns {void}
 */
export const displayQuestionGroupLogicUpdatedToast = () => {
  toast(
    <p>
      Some logic in this questionnaire has been updated or removed due to a question group change.
    </p>
  );
};

/**
 * groupSubtreeIds
 * Ids of `groupId` plus every group nested beneath it, at any depth. Ids already collected are
 * never revisited, so a parentGroup cycle terminates rather than looping.
 */
const groupSubtreeIds = (groupId, groups) => {
  const groupIds = new Set([groupId]);
  const groupIdsToCheck = [groupId];
  while (groupIdsToCheck.length > 0) {
    const currentId = groupIdsToCheck.pop();
    Object.values(groups).forEach(({ id, parentGroup }) => {
      if (parentGroup !== currentId || groupIds.has(id)) return;
      groupIds.add(id);
      groupIdsToCheck.push(id);
    });
  }
  return groupIds;
};

/**
 * lastGroupMemberIndex
 * Index of the last question in `questions` that belongs to `groupId` or any group nested beneath
 * it (-1 if none). Used to keep a group's questions — and its nested groups' questions — contiguous
 * when inserting into the flat question list.
 */
const lastGroupMemberIndex = (questions, groupId, groups) => {
  if (groupId == null) return -1;
  const groupIds = groupSubtreeIds(groupId, groups);
  return questions.reduce(
    (lastIndex, question, index) => (groupIds.has(question.questionGroup) ? index : lastIndex),
    -1
  );
};

/**
 * planEmptyGroupCleanup
 * Given the post-change questions and groups, finds every group left without a direct member
 * question and returns the flattening plan for it: the groups to delete, plus the surviving child
 * groups that must be re-parented (to the deleted group's own parent, or the nearest ancestor that
 * is itself surviving) so no group is ever left pointing at a deleted parent.
 * @param {Array} questions flat post-change question list
 * @param {Object} groups questionGroups keyed by stringified id
 * @return {{ orphanIds: string[], reparents: Array<{ groupId: number, parentGroup: number|null }> }}
 */
const planEmptyGroupCleanup = (questions, groups) => {
  const occupiedGroupIds = new Set(
    questions
      .filter(({ questionGroup }) => questionGroup != null)
      .map(({ questionGroup }) => String(questionGroup))
  );
  const orphanIds = Object.keys(groups).filter((id) => !occupiedGroupIds.has(id));
  const orphanIdSet = new Set(orphanIds);

  // Nearest ancestor that isn't itself being deleted (null once we run out). The visited set guards
  // against a parentGroup cycle, which would otherwise spin forever.
  const surviving = (groupId) => {
    const visited = new Set();
    let currentId = groupId == null ? null : String(groupId);
    while (currentId != null && orphanIdSet.has(currentId) && !visited.has(currentId)) {
      visited.add(currentId);
      const parentGroup = groups[currentId]?.parentGroup;
      currentId = parentGroup == null ? null : String(parentGroup);
    }
    return currentId == null || orphanIdSet.has(currentId) ? null : Number(currentId);
  };

  const reparents = Object.values(groups)
    .filter(
      ({ id, parentGroup }) =>
        parentGroup != null && orphanIdSet.has(String(parentGroup)) && !orphanIdSet.has(String(id))
    )
    .map(({ id, parentGroup }) => ({ groupId: id, parentGroup: surviving(parentGroup) }));

  return { orphanIds, reparents };
};

/**
 * flattenEmptyGroups
 * Commits `questions` + the flattening plan for any group they leave empty: optimistically updates
 * survey state, then re-parents surviving children before deleting the emptied groups on the
 * backend. Anything the backend still holds after a failure is restored to local state so the two
 * don't drift.
 * @param {Object} params
 * @param {Object} params.groups questionGroups keyed by stringified id, post-change
 * @param {boolean} params.markDirty whether the question-list change should mark the draft dirty
 * @param {Object} [params.questionLogic] questionLogic to write alongside questions/groups
 * @param {Array} params.questions flat post-change question list
 * @param {Object} params.surveyRef ref whose `.current` holds the live survey
 * @param {Function} params.updateSurvey survey state updater
 */
const flattenEmptyGroups = async ({
  groups,
  markDirty,
  questionLogic,
  questions,
  surveyRef,
  updateSurvey,
}) => {
  const { orphanIds, reparents } = planEmptyGroupCleanup(questions, groups);

  const nextGroups = { ...groups };
  reparents.forEach(({ groupId, parentGroup }) => {
    nextGroups[String(groupId)] = { ...groups[String(groupId)], parentGroup };
  });
  orphanIds.forEach((id) => delete nextGroups[id]);

  updateSurvey(
    { questionGroups: nextGroups, questions, ...(questionLogic ? { questionLogic } : {}) },
    { markDirty }
  );

  if (orphanIds.length === 0) return;

  const reparentResults = await Promise.allSettled(
    reparents.map(({ groupId, parentGroup }) => updateQuestionGroup({ id: groupId, parentGroup }))
  );
  const failedReparents = reparents.filter(
    (_, index) => reparentResults[index].status === 'rejected'
  );
  // A group whose child could not be re-parented has to stay — deleting it would orphan that child.
  const blockedOrphanIds = new Set(
    failedReparents.map(({ groupId }) => String(groups[String(groupId)]?.parentGroup))
  );
  const deletableOrphanIds = orphanIds.filter((id) => !blockedOrphanIds.has(id));

  const deleteResults = await Promise.allSettled(
    deletableOrphanIds.map((id) => deleteQuestionGroup({ id: Number(id) }))
  );
  const failedDeleteIds = deletableOrphanIds.filter(
    (id, index) => deleteResults[index].status === 'rejected'
  );

  const retainedGroupIds = [
    ...blockedOrphanIds,
    ...failedDeleteIds,
    ...failedReparents.map(({ groupId }) => String(groupId)),
  ];
  if (retainedGroupIds.length === 0) return;

  updateSurvey(
    {
      questionGroups: {
        ...(surveyRef.current.questionGroups || {}),
        ...Object.fromEntries(retainedGroupIds.map((id) => [id, groups[id]])),
      },
    },
    { markDirty: false }
  );
  toast.error('Unable to delete an empty question group. Please refresh and try again.');
};

/**
 * useQuestionGroupHelpers
 * Question group create/rename/randomize/assign/nest callbacks. Read survey state via surveyRef so
 * callbacks don't need survey in deps.
 * @param {Object} params
 * @param {Object} params.surveyRef ref whose `.current` holds the live survey
 * @param {Function} params.updateSurvey survey state updater
 * @return {{
 *   assignGroupToParent: Function,
 *   assignQuestionToGroup: Function,
 *   createGroupForQuestion: Function,
 *   renameGroup: Function,
 *   toggleGroupRandomize: Function,
 *   toggleQuestionAnchored: Function,
 * }}
 */
export const useQuestionGroupHelpers = ({ surveyRef, updateSurvey }) => {
  const createGroupForQuestion = useCallback(
    async (question) => {
      const targetKey = createQuestionKey(question);

      // Groups are created without a name prompt, so the name has to be unique by construction.
      let name = DEFAULT_QUESTION_GROUP_NAME;
      let nameSuffix = 2;
      while (isQuestionGroupNameTaken({ name, questionGroups: surveyRef.current.questionGroups })) {
        name = `${DEFAULT_QUESTION_GROUP_NAME} ${nameSuffix}`;
        nameSuffix += 1;
      }

      const group = await createQuestionGroup({ name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(group.id)]: group,
          },
          questions: surveyRef.current.questions.map((q) =>
            createQuestionKey(q) === targetKey ? { ...q, questionGroup: group.id } : q
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  const renameGroup = useCallback(
    async (groupId, name) => {
      await updateQuestionGroup({ id: groupId, name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(surveyRef.current.questionGroups?.[String(groupId)] || { id: groupId }),
              name,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  const toggleGroupRandomize = useCallback(
    async (groupId) => {
      const existing = surveyRef.current.questionGroups?.[String(groupId)];
      const nextRandomize = !(existing?.randomizeOrder ?? false);
      await updateQuestionGroup({ id: groupId, randomizeOrder: nextRandomize });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(existing || { id: groupId }),
              randomizeOrder: nextRandomize,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Nest a group under a parent (parentGroupId) or detach it (null).
  const assignGroupToParent = useCallback(
    async (groupId, parentGroupId) => {
      const updatedGroup = await updateQuestionGroup({
        id: groupId,
        parentGroup: parentGroupId,
      });
      const latestGroups = surveyRef.current.questionGroups || {};
      const latestQuestions = surveyRef.current.questions;
      const previousParentGroupId = latestGroups[String(groupId)]?.parentGroup ?? null;
      // The whole subtree moves as one run. A group left sitting inside its former parent's run
      // splits that parent's block, and the backend rejects the save with "Questions in group N
      // must be adjacent".
      const movingGroupIds = groupSubtreeIds(groupId, latestGroups);
      const movingQuestions = latestQuestions.filter(({ questionGroup }) =>
        movingGroupIds.has(questionGroup)
      );
      let updatedQuestions = latestQuestions;

      const anchorGroupId = parentGroupId ?? previousParentGroupId;
      if (anchorGroupId != null && movingQuestions.length > 0) {
        const withoutMovingQuestions = latestQuestions.filter(
          ({ questionGroup }) => !movingGroupIds.has(questionGroup)
        );
        const lastAnchorQuestionIndex = lastGroupMemberIndex(
          withoutMovingQuestions,
          anchorGroupId,
          latestGroups
        );
        const insertAt =
          lastAnchorQuestionIndex === -1
            ? withoutMovingQuestions.length
            : lastAnchorQuestionIndex + 1;
        const reorderedQuestions = [...withoutMovingQuestions];
        reorderedQuestions.splice(insertAt, 0, ...movingQuestions);
        // Avoid marking draft as dirty if order hasn't changed.
        if (reorderedQuestions.some((question, index) => question !== latestQuestions[index])) {
          updatedQuestions = reorderedQuestions;
        }
      }

      const nextGroups = {
        ...latestGroups,
        [String(groupId)]: updatedGroup,
      };

      const { changed: logicChanged, questionLogic: prunedQuestionLogic } = pruneInvalidLogic({
        questionGroups: nextGroups,
        questionLogic: surveyRef.current.questionLogic || {},
        questions: updatedQuestions,
      });

      await flattenEmptyGroups({
        groups: nextGroups,
        markDirty: updatedQuestions !== latestQuestions,
        questionLogic: logicChanged ? prunedQuestionLogic : undefined,
        questions: updatedQuestions,
        surveyRef,
        updateSurvey,
      });

      if (logicChanged) {
        displayQuestionGroupLogicUpdatedToast();
      }
    },
    [surveyRef, updateSurvey]
  );

  const toggleQuestionAnchored = useCallback(
    (question) => {
      const targetQuestionKey = createQuestionKey(question);
      updateSurvey(
        {
          questions: surveyRef.current.questions.map((currentQuestion) =>
            createQuestionKey(currentQuestion) === targetQuestionKey
              ? { ...currentQuestion, anchored: !currentQuestion.anchored }
              : currentQuestion
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Move a question into a group (groupId) or out of its group (null), repositioning it so group
  // boundaries stay contiguous:
  //  - into a group  → inserted as the group's LAST member;
  //  - out of a group → moved to directly below its old group's run (the question is kept).
  // Groups left without a direct member question are flattened away: surviving child groups are
  // re-parented up a level and the emptied group is deleted.
  const assignQuestionToGroup = useCallback(
    async (question, groupId) => {
      const targetKey = createQuestionKey(question);
      const questions = surveyRef.current.questions;
      const moving = questions.find((q) => createQuestionKey(q) === targetKey);
      if (!moving) return;
      const oldGroupId = moving.questionGroup ?? null;
      const originalIndex = questions.findIndex((q) => createQuestionKey(q) === targetKey);

      const without = questions.filter((q) => createQuestionKey(q) !== targetKey);
      const currentGroups = surveyRef.current.questionGroups || {};

      let insertAt;
      if (groupId != null) {
        const lastIdx = lastGroupMemberIndex(without, groupId, currentGroups);
        insertAt = lastIdx === -1 ? without.length : lastIdx + 1;
      } else {
        // Ungrouping: drop directly below the old group's run; if that group is now empty (or the
        // question had no group), keep it where it was.
        const lastIdx = lastGroupMemberIndex(without, oldGroupId, currentGroups);
        insertAt = lastIdx === -1 ? Math.min(originalIndex, without.length) : lastIdx + 1;
      }

      const updatedQuestions = [...without];
      updatedQuestions.splice(insertAt, 0, {
        ...moving,
        anchored: groupId == null ? false : moving.anchored,
        questionGroup: groupId,
      });

      const { changed: logicChanged, questionLogic: prunedQuestionLogic } = pruneInvalidLogic({
        questionGroups: currentGroups,
        questionLogic: surveyRef.current.questionLogic || {},
        questions: updatedQuestions,
      });

      await flattenEmptyGroups({
        groups: currentGroups,
        markDirty: true,
        questionLogic: logicChanged ? prunedQuestionLogic : undefined,
        questions: updatedQuestions,
        surveyRef,
        updateSurvey,
      });

      if (logicChanged) {
        displayQuestionGroupLogicUpdatedToast();
      }
    },
    [surveyRef, updateSurvey]
  );

  return {
    assignGroupToParent,
    assignQuestionToGroup,
    createGroupForQuestion,
    renameGroup,
    toggleGroupRandomize,
    toggleQuestionAnchored,
  };
};
